import { useRef, type ChangeEvent } from "react";
import { useUploadTellescopeFile, useTellescopeMutation, useAuth, useUser } from "@tellescope/tanstack-query";
import { Button, Avatar, AvatarImage, AvatarFallback } from "@tellescope/ui";
import { formatInitials } from "@tellescope/utils";
import { Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSecureFileUrl } from "@tellescope/tanstack-query";

export interface AvatarUploadProps {
  userId?: string;
}

export const AvatarUpload = ({ userId: providedUserId }: AvatarUploadProps) => {
  const { t } = useTranslation("auth");
  const { currentUser } = useAuth();
  const providedUser = useUser(providedUserId ?? "", !!providedUserId);
  const user = providedUserId ? providedUser : currentUser;
  const actualUserId = user?.id;

  const avatarUrl = useSecureFileUrl(user?.avatar);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    uploadFileAsync,
    stage,
    progress,
    prepareError,
    uploadError,
    confirmError,
    validationError,
    reset: resetUpload,
  } = useUploadTellescopeFile({
    autoConfirm: true,
    returnDownloadURL: true,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    allowedFileTypes: ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"],
  });

  const updateUserMutation = useTellescopeMutation({
    model: "users",
    method: "updateOne",
  });

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !actualUserId) return;

    resetUpload();

    try {
      const uploadResult = await uploadFileAsync(
        {
          name: `avatar-${actualUserId}-${Date.now()}`,
          size: file.size,
          type: file.type,
          enduserId: actualUserId,
        },
        file
      );

      await updateUserMutation.mutateAsync({
        id: actualUserId,
        avatar: uploadResult.secureName || uploadResult.downloadURL,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Avatar upload failed:", error);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!actualUserId) return;

    try {
      await updateUserMutation.mutateAsync({
        id: actualUserId,
        avatar: "",
      });
    } catch (error) {
      console.error("Failed to remove avatar:", error);
    }
  };

  if (!actualUserId || !user) return null;

  const hasAvatar = !!user.avatar;
  const isLoading = stage !== "idle" || updateUserMutation.isPending;

  return (
    <div className="flex flex-col items-center gap-4 p-4 border rounded-lg">
      <div className="relative">
        <Avatar className="h-24 w-24">
          <AvatarImage key={user?.avatar || "no-avatar"} src={avatarUrl} alt={user?.displayName || "Avatar"} />
          <AvatarFallback useStaticBackground={true} id={user?.id}>
            {formatInitials(user)}
          </AvatarFallback>
        </Avatar>
        {hasAvatar && !isLoading && (
          <button
            onClick={handleRemoveAvatar}
            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
            title={t("Remove avatar")}
          >
            <X size={12} />
          </button>
        )}
      </div>

      <div className="text-center">
        <Button onClick={handleFileSelect} disabled={isLoading} variant="outline" size="sm" className="mb-2">
          <Upload size={16} className="mr-2" />
          {hasAvatar ? t("Change Image") : t("Upload Image")}
        </Button>

        {isLoading && (
          <div className="text-sm text-muted-foreground">
            {stage === "preparing" && t("Preparing upload...")}
            {stage === "uploading" && t("Uploading... {{progress}}%", { progress })}
            {stage === "confirming" && t("Confirming upload...")}
            {updateUserMutation.isPending && t("Updating profile...")}
          </div>
        )}

        {(validationError || prepareError || uploadError || confirmError || updateUserMutation.error) && (
          <div className="text-sm text-red-600 mt-1">
            {validationError ? validationError.message : t("Upload failed. Please try again.")}
          </div>
        )}

        <div className="text-xs text-muted-foreground mt-1">{t("Images up to 5MB (JPG, PNG, GIF, WebP)")}</div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
    </div>
  );
};
